/**
 * FAQ section for checkout page
 */

import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ChevronDown, HelpCircle } from 'lucide-react'
import { Button } from '@/components/ui'
import { tr } from '@/utils/translate'

interface FAQItem {
  question: string
  answer: string
  translations?: Record<string, Record<string, string>>
}

interface CheckoutFAQProps {
  items?: FAQItem[]
  restaurantPhone?: string
  restaurantEmail?: string
}

function useDefaultFAQItems(): FAQItem[] {
  const { t } = useTranslation()
  return [
    { question: t('faqDefaultQ1'), answer: t('faqDefaultA1') },
    { question: t('faqDefaultQ2'), answer: t('faqDefaultA2') },
    { question: t('faqDefaultQ3'), answer: t('faqDefaultA3') },
    { question: t('faqDefaultQ4'), answer: t('faqDefaultA4') },
    { question: t('faqDefaultQ5'), answer: t('faqDefaultA5') },
    { question: t('faqDefaultQ6'), answer: t('faqDefaultA6') },
  ]
}

function FAQAccordionItem({
  item,
  isOpen,
  onToggle,
  lang,
}: {
  item: FAQItem
  isOpen: boolean
  onToggle: () => void
  lang: string
}) {
  return (
    <div className="border-b border-border">
      <Button
        variant="ghost"
        className="w-full flex items-center justify-between py-4 h-auto text-left hover:text-primary rounded-sm px-0 hover:bg-transparent"
        onClick={onToggle}
        aria-expanded={isOpen}
      >
        <span className="font-medium pr-4">{tr(item.translations, 'question', lang, item.question)}</span>
        <ChevronDown
          className={`h-5 w-5 flex-shrink-0 text-muted-foreground transition-transform ${
            isOpen ? 'rotate-180' : ''
          }`}
        />
      </Button>
      {isOpen && (
        <div className="pb-4 text-sm text-muted-foreground animate-fade-in">
          {tr(item.translations, 'answer', lang, item.answer)}
        </div>
      )}
    </div>
  )
}

export function CheckoutFAQ({ items, restaurantPhone, restaurantEmail }: CheckoutFAQProps) {
  const { t, i18n } = useTranslation()
  const lang = i18n.language
  const defaultItems = useDefaultFAQItems()
  const resolvedItems = items && items.length > 0 ? items : defaultItems
  const [openIndex, setOpenIndex] = useState<number | null>(null)

  const toggleItem = (index: number) => {
    setOpenIndex(openIndex === index ? null : index)
  }

  return (
    <section className="border-t pt-12 mt-12">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <HelpCircle className="h-6 w-6 text-primary" />
          <div>
            <h2 className="text-2xl font-bold">{t('faqTitle')}</h2>
            <p className="text-sm text-muted-foreground mt-1">
              {t('faqSubtitle')}
            </p>
          </div>
        </div>

        {/* FAQ Accordion */}
        <div className="space-y-0">
          {resolvedItems.map((item, index) => (
            <FAQAccordionItem
              key={index}
              item={item}
              isOpen={openIndex === index}
              onToggle={() => toggleItem(index)}
              lang={lang}
            />
          ))}
        </div>

        {/* Contact section — only shown when restaurant provides phone or email */}
        {(restaurantPhone || restaurantEmail) && (
          <div className="mt-8 p-4 bg-muted rounded-lg text-center">
            <p className="text-sm text-muted-foreground">
              {t('faqStillHaveQuestions')}{' '}
              {restaurantPhone && (
                <Button variant="link" className="h-auto p-0 text-sm font-medium" asChild>
                  <a href={`tel:${restaurantPhone}`}>{t('faqCallUs')}</a>
                </Button>
              )}
              {restaurantPhone && restaurantEmail && ` ${t('faqOr')} `}
              {restaurantEmail && (
                <Button variant="link" className="h-auto p-0 text-sm font-medium" asChild>
                  <a href={`mailto:${restaurantEmail}`}>{t('faqEmailUs')}</a>
                </Button>
              )}
            </p>
          </div>
        )}
      </div>
    </section>
  )
}
